import { Component, OnInit, Output, EventEmitter } from "@angular/core";
import { UploadMultipleFilesService } from "@fiduciaria/RequestCreation/Services/upload-multiple-files.service";
import { DocumentFile } from "@fiduciaria/RequestCreation/Types/document-file";
import { BsModalRef } from "ngx-bootstrap/modal";
import { NgxFileDropEntry } from "ngx-file-drop";
import { Subject } from "rxjs";

import { ModalTitles, ModalType } from "@shared/Constantes/modal.constantes";
import { File } from "@shared/Entities/General/file.entity";
import { FileHandlerService } from "@shared/Services/fileHandler.service";
import { ModalService } from "@shared/Services/modal.service";

@Component({
  selector: "app-upload-multiple-documents-modal",
  templateUrl: "./upload-multiple-documents-modal.component.html",
  styleUrls: ["./upload-multiple-documents-modal.component.scss"],
})
export class UploadMultipleDocumentsModalComponent implements OnInit {
  public files: DocumentFile[];
  public currentFilesUpload: DocumentFile[];
  public requestFiles: DocumentFile[];
  public accept: string;
  public reference: string;
  public file = new Subject<DocumentFile | DocumentFile[]>();
  public fileNameToName: (file: File) => string;
  public fileNameToExtension: (file: File) => string;
  public getFileSizeMegaBytes: (file: File, digits: number) => number;
  public bytesToMegaBytes: (bytes: number) => number;
  public getSizeMegaBytes: (bytes: number, digits: number) => number;

  public filesUpload: DocumentFile[];
  public newFilesUpload: DocumentFile[];
  private filesFullSizeBytes: number;
  private filesFullSizeMegaBytes: number;
  private filesFullSizeMegaBytesRounded: number;
  private selectedFiles: DocumentFile[];
  private inputFileIdentifier: string;
  private onConfirm: Subject<boolean>;
  private documentsTemp: DocumentFile[];
  public firstFile: DocumentFile;
  public DroppedFilesCount: number;

  @Output() public filesUploadEvent: EventEmitter<DocumentFile[]> = new EventEmitter<DocumentFile[]>();
  @Output() public filesFullSizeBytesEvent: EventEmitter<number> = new EventEmitter<number>();
  @Output() public filesFullSizeMegaBytesEvent: EventEmitter<number> = new EventEmitter<number>();

  constructor(
    private bsModalRef: BsModalRef,
    private fileHandlerService: FileHandlerService,
    private modalService: ModalService,
    private uploadMultipleFilesService: UploadMultipleFilesService
  ) {}

  public ngOnInit(): void {
    this.filesUpload = [];
    this.newFilesUpload = [];
    this.filesFullSizeBytes = 0;
    this.filesFullSizeMegaBytes = 0;
    this.filesFullSizeMegaBytesRounded = 0;
    this.selectedFiles = [];
    this.inputFileIdentifier = "input-check-new-file";
    this.onConfirm = new Subject();
    this.documentsTemp = [];
    this.firstFile = null;
    this.DroppedFilesCount = 0;

    if (this.currentFilesUpload.length) this.newFilesUpload = this.currentFilesUpload;

    this.filesUpload = this.files;
    this.filesUpload.reverse();

    this.setFilesFullSize();
  }

  public get getAcceptedFormat(): string {
    return this.accept;
  }

  public setFilesFullSize(resetCurrentValue: boolean = true): void {
    if (resetCurrentValue) this.filesFullSizeBytes = 0;

    this.filesUpload.forEach((fileUpload) => {
      this.filesFullSizeBytes += fileUpload?.file?.object?.size ?? 0;
    });

    const filesUploadedExcluded: DocumentFile[] = [];

    this.newFilesUpload
      .filter((x) => !this.filesUpload.includes(x))
      .forEach((fileUpload) => {
        this.filesFullSizeBytes += fileUpload?.file?.object?.size ?? 0;
        filesUploadedExcluded.push(fileUpload);
      });

    this.filesFullSizeMegaBytes = this.bytesToMegaBytes(this.filesFullSizeBytes);
    this.filesFullSizeMegaBytesRounded = this.uploadMultipleFilesService.getFilesSizeMegaBytes1X1(
      this.filesUpload.concat(filesUploadedExcluded),
      3
    );
  }

  public onFileDrop(files: NgxFileDropEntry[]): void {
    this.validateFiles(files, this.reference);
  }

  private validateFiles(files: NgxFileDropEntry[], reference: string): void {
    const newDocuments: DocumentFile[] = [];
    const service = this.uploadMultipleFilesService;
    const filesAll: DocumentFile[] = this.requestFiles;
    this.DroppedFilesCount = files.filter((file) => file.fileEntry.isFile).length;

    for (const fl of files) {
      if (!fl.fileEntry.isFile) continue;

      const fileEntry: FileSystemFileEntry = fl.fileEntry as FileSystemFileEntry;
      fileEntry.file((file) => {
        const newFile: File = {
          object: file,
          name: fileEntry.name,
          buffer: "",
        };

        const document: DocumentFile = { name: reference, file: newFile };
        newDocuments.push(document);

        if (newDocuments.length === files.length) {
          this.documentsTemp = this.documentsTemp.concat(newDocuments);
          const documents = filesAll.concat(this.documentsTemp);

          const validations = service.validateFilesSizeMegaBytes(documents);

          this.documentsTemp = this.documentsTemp.filter((x) => !newDocuments.some((y) => y.file === x.file));

          const [, invalid] = validations;

          if (!invalid) {
            this.appendFilesBeforeValidation(files);
          } else {
            this.uploadMultipleFilesService.filesSizeValidationsAlert(validations);
          }
        }
      });
    }
  }

  private appendFilesBeforeValidation(files: NgxFileDropEntry[]) {
    files.forEach((fl) => {
      if (fl.fileEntry.isFile) {
        const fileEntry: FileSystemFileEntry = fl.fileEntry as FileSystemFileEntry;
        fileEntry.file((file) => {
          this.setNewFile(file, fileEntry.name);
        });
      }
    });
  }

  private onKeyPressCheck(setSelectedFiles: () => void, $event: KeyboardEvent) {
    const keyCode: string = $event?.code ?? "";
    const isEnterkey = keyCode && keyCode.toLowerCase() === "enter";
    const target: EventTarget = $event?.target;
    const targetId: string = (target["id"] ?? "").trim();
    const isChecked: boolean = target["checked"] === true;
    const element: HTMLElement = document.querySelector(`#${targetId}`);

    if (isEnterkey && targetId && element) {
      element["checked"] = !isChecked;
      setSelectedFiles();
    }
  }

  public clickBtnSelectFiles(): void {
    const btnSelectFiles: HTMLElement = document.querySelector(`button[id="btn-select-documents"]`);
    if (btnSelectFiles) btnSelectFiles.click();
  }

  public onSave(): void {
    this.onConfirm.next(true);
    this.bsModalRef.hide();
  }

  public onCancel(): void {
    this.onConfirm.next(false);
    this.bsModalRef.hide();
  }

  public setNewFile(file: globalThis.File, name: string): void {
    this.fileHandlerService.getBase64ArrayFromBuffer(file).subscribe((array) => {
      const newFile = {
        object: file,
        name: name,
        buffer: this.fileHandlerService.stripBase64StuffFromImage(array),
      };

      const document: DocumentFile = { name: this.reference, file: newFile };

      if (!this.firstFile && !this.files?.length) {
        this.firstFile = document;
      }

      this.filesUpload = [document, ...this.filesUpload];
      this.newFilesUpload = [document, ...this.newFilesUpload];
      this.documentsTemp = this.newFilesUpload;
      this.file.next(this.filesUpload);
      this.setFilesFullSize();
    });
  }

  private removeFile(file: File, useConfirmDialog: boolean = true): void {
    const removeAFile: (file: File) => void = (file: File): void => {
      this.filesUpload = this.filesUpload.filter((x) => x.file !== file);
      this.newFilesUpload = this.newFilesUpload.filter((x) => x.file !== file);
      if (this.selectedFiles.some((x) => x.file === file)) this.selectedFiles = this.selectedFiles.filter((x) => x.file !== file);
      this.documentsTemp = this.newFilesUpload;
      this.setFilesFullSize();

      if (this.firstFile && !this.files?.length) {
        this.firstFile = this.newFilesUpload?.length ? this.newFilesUpload.slice(-1)[0] : null;
      }
    };

    if (!useConfirmDialog) {
      removeAFile(file);
      return;
    }

    const msg: string = `Quiere remover el archivo <b>${file.name}</b>`;
    this.modalService
      .showConfirm({
        title: ModalTitles.warning,
        body: `¿${msg}?`,
        type: ModalType.warning,
      })
      .subscribe((ok) => {
        if (ok) removeAFile(file);
      });
  }

  public removeSelectedFiles(removeFiles: boolean = true): void {
    const totalFiles: number = this.selectedFiles.length ?? 0;
    const selectedText: string = totalFiles === 1 ? " seleccionado" : " seleccionados";
    let msg: string = "Quiere";
    let list: string = "";

    msg += removeFiles ? " remover" : " deseleccionar";
    msg += totalFiles === 1 ? " el archivo" : ` los <b>${totalFiles}</b> archivos`;
    msg += selectedText;

    this.selectedFiles.forEach((file, index) => {
      list += `<span>${index + 1})&nbsp;<b>${file?.file?.name}</b></span><br/>`;
    });

    const msgBody = `¿${msg}?<br/>${list}`;

    const process: () => void = () => {
      if (removeFiles) this.selectedFiles.forEach((file) => this.removeFile(file.file, false));
      if (!removeFiles) this.IfSelectedFileCheckit(false);
      this.selectedFiles = [];
    };

    this.modalService
      .showConfirm({
        title: ModalTitles.warning,
        body: msgBody,
        type: ModalType.warning,
      })
      .subscribe((ok) => {
        if (ok) process();
      });
  }

  private ifEveryNewFileIsSelected(): boolean {
    return this.newFilesUpload.every((file) => this.selectedFiles.some((x) => x.file === file.file));
  }

  private IfSelectedFile(file: File): boolean {
    return this.selectedFiles.some((x) => x.file === file);
  }

  private IfSelectedFileCheckit(checked: boolean = true): void {
    this.selectedFiles.forEach((file) => {
      const index = this.newFilesUpload.findIndex((x) => x.file === file.file);
      const idInputFileCheck = `${this.inputFileIdentifier}-${index}`;
      this.markSelectedFilesRows(idInputFileCheck, checked);
    });
  }

  private setSelectedFilesAll(): void {
    this.selectedFiles = this.newFilesUpload;
    this.IfSelectedFileCheckit();
  }

  private setSelectedFiles(file: File, event: Event): void {
    const target: EventTarget = event?.target;
    const targetId: string = target["id"] ?? "";
    const isChecked: boolean = target["checked"] === true;

    this.selectedFiles = this.selectedFiles.filter((x) => x.file !== file);

    const document: DocumentFile = { name: this.reference, file };
    if (isChecked) this.selectedFiles.push(document);

    this.markSelectedFilesRows(targetId, isChecked);
  }

  private markSelectedFilesRows(idInputFileCheck: string, checked: boolean = false): void {
    const bgColor: string = checked ? "dodgerblue" : "transparent";
    const color: string = checked ? "white" : "black";
    const border: string = checked ? "2px solid white" : "none";
    const border2: string = checked ? "2px solid white" : "2px solid rgba(0, 0, 0, 0.05)";

    const [_trStylesClass, _btnRemoveStylesId]: string[] = [`${idInputFileCheck}-rows`, `${idInputFileCheck}-btn-remove`];

    const inputFileCheck: HTMLElement | undefined | null = document.getElementById(idInputFileCheck);
    if (inputFileCheck) inputFileCheck["checked"] = checked;

    const btnRemove: HTMLElement | undefined | null = document.getElementById(_btnRemoveStylesId);
    if (btnRemove) btnRemove.style.border = border;

    const trCollection: HTMLCollection | undefined | null = document.getElementsByClassName(_trStylesClass) as HTMLCollection;
    const trLastCollection: HTMLCollection | undefined | null = document.getElementsByClassName(
      `${_trStylesClass}-last`
    ) as HTMLCollection;

    const trElementArray: Array<HTMLElement> = trCollection ? [].slice.call(trCollection) : [];
    const trLastElementArray: Array<HTMLElement> = trLastCollection ? [].slice.call(trLastCollection) : [];

    for (const trElement of trElementArray) {
      trElement.style.backgroundColor = bgColor;
      trElement.style.color = color;
    }

    for (const trLastElement of trLastElementArray) trLastElement.style.borderBottom = border2;
  }

  public closeModal = (): void => this.bsModalRef.hide();
}
